<template>
  <div id="page-product-form">
    <vs-row>
      <vs-col>
        <card-page :card-title="cardTitle" :switch-model="model.IsEnabled">
          <vue-element-loading :active="busy"></vue-element-loading>
          <vue-form-generator
              ref="form"
              :model="model"
              :options="formOptions"
              :schema="schema"
              :class="readonly ? 'readonly-form' : ''"
          />
        </card-page>
      </vs-col>
    </vs-row>
  </div>
</template>

<script>

import FormModelMixin from '@/components/mixins/FormModelMixin';
import { selectOptions, datepickerOptions } from '@/components/form/FormElementOption';
import FormFieldValidationMixin from "@/components/mixins/FormFieldValidationMixin";
import moment from 'moment';
import CardPage from '../../../components/Views/CardPage';

export default {
  name: 'MarketForm',
  props: {
    value: {
      type: Object,
      required: true
    },
    cardTitle: {
      type: String,
      required: false,
    },
    readonly: {
      type: Boolean,
      required: false,
      default: false
    },
    loading: {
      type: Boolean,
      required: false,
      default: false,
    }
  },
  components: {
    CardPage,
  },
  mixins: [FormModelMixin, FormFieldValidationMixin],
  data() {
    return {
      model: {
        MarketDescription: null,
        TotalPercentage: null,
        TaxDuetoCoverholder: 0,
        InceptionDate: null,
        ExpiryDate: null,
        TotalDeduction_PC: null,
        Coverholder_PC: null,
        FacilityManager_PC: null,
        Broker_PC: null,
        SlipLeaderFee_PC: null,
        NetOfUnderwriter_PC: null,
      },
      schema: {
        groups: [
          {
            styleClasses: 'w-full',
            legend: 'General',
            fields: [
              {
                disabled: () => this.readonly,
                type: 'input',
                inputType: 'text',
                styleClasses: 'vx-col w-1/3',
                label: 'Name',
                model: 'MarketName',
                required: true,
                validator: ['required', 'string', async (value, field, model) => {
                  if (value.trim() === '') {
                    return 'Only white spaces detected';
                  }
                }],
              },
              {
                disabled: () => this.readonly,
                type: 'input',
                inputType: 'text',
                styleClasses: 'vx-col w-1/3',
                label: 'Description',
                model: 'MarketDescription',
                required: true,
                validator: ['required', 'string', async (value, field, model) => {
                  if (value.trim() === '') {
                    return 'Only white spaces detected';
                  }
                }],
              },
              {
                disabled: () => this.readonly,
                type: 'input',
                inputType: 'text',
                styleClasses: 'vx-col w-1/3',
                label: 'Company Insurer',
                model: 'CompanyInsurer',
                validator: 'string',
              },
              {
                disabled: () => this.readonly,
                type: 'input',
                inputType: 'number',
                styleClasses: 'vx-col w-1/3',
                label: 'Total Market %',
                model: 'TotalPercentage',
                required: true,
                validator: ['required', 'positive', 'numeric'],
              },
            ]
          },
          {
            styleClasses: 'w-full',
            legend: 'Dates',
            fields: [
              {
                ...datepickerOptions,
                disabled: () => this.readonly,
                styleClasses: 'vx-col w-1/3',
                label: 'Start of Effect',
                model: 'InceptionDate',
                required: true,
                onChanged: () => {
                  this.validateField('ExpiryDate');
                },
                validator: ['required', (value, field, model) => {
                  if (model.ExpiryDate) {
                    let expiryDate = model.ExpiryDate;
                    let inceptionDate = moment(model.InceptionDate).unix();

                    if (typeof expiryDate === 'string') {
                      expiryDate = moment(model.ExpiryDate, "YYYY-MM-DD").unix();
                    } else {
                      expiryDate = expiryDate / 1000;
                    }

                    if (inceptionDate > expiryDate) {
                      return 'Start of Effect must be before End of Effect';
                    }
                  }

                  return true;
                }],
              },
              {
                disabled: () => this.readonly,
                ...datepickerOptions,
                styleClasses: 'vx-col w-1/3',
                label: 'End of Effect',
                model: 'ExpiryDate',
                required: true,
                onChanged: () => {
                  this.validateField('InceptionDate');
                },
                validator: ['string', (value, field, model) => {
                  if (model.InceptionDate) {
                    let inceptionDate = model.InceptionDate;
                    let expiryDate = moment(model.ExpiryDate).unix();

                    if (typeof inceptionDate === 'string') {
                      inceptionDate = moment(model.InceptionDate, "YYYY-MM-DD").unix();
                    } else {
                      inceptionDate = inceptionDate / 1000;
                    }

                    if (expiryDate < inceptionDate) {
                      return 'End of Effect must be after Start of Effect.';
                    }
                  }

                  return true;
                }],
              },
            ]
          },
          {
            styleClasses: 'w-full',
            legend: 'Figures',
            fields: [
              {
                disabled: () => this.readonly,
                type: 'input',
                inputType: 'number',
                styleClasses: 'vx-col w-1/3',
                label: 'Total Deduction',
                model: 'TotalDeduction_PC',
                required: true,
                validator: ['required', 'positive', 'numeric'],
              },
              {
                disabled: () => this.readonly,
                type: 'input',
                inputType: 'number',
                styleClasses: 'vx-col w-1/3',
                label: 'Coverholder %',
                model: 'Coverholder_PC',
                required: true,
                validator: ['required', 'positive', 'numeric'],
              },
              {
                disabled: () => this.readonly,
                type: 'input',
                inputType: 'number',
                styleClasses: 'vx-col w-1/3',
                label: 'Facility Manager %',
                model: 'FacilityManager_PC',
                required: true,
                validator: ['required', 'positive', 'numeric'],
              },
            ]
          },
          {
            styleClasses: 'w-full',
            fields: [
              {
                disabled: () => this.readonly,
                type: 'input',
                inputType: 'number',
                styleClasses: 'vx-col w-1/3',
                label: 'Broker %',
                model: 'Broker_PC',
                required: true,
                validator: ['required', 'positive', 'numeric'],
              },
              {
                disabled: () => this.readonly,
                type: 'input',
                inputType: 'number',
                styleClasses: 'vx-col w-1/3',
                label: 'SLF %',
                model: 'SlipLeaderFee_PC',
                required: true,
                validator: ['required', 'positive', 'numeric'],
              },
              {
                disabled: () => this.readonly,
                type: 'input',
                inputType: 'number',
                styleClasses: 'vx-col w-1/3',
                label: 'Net to Underwriter %',
                model: 'NetOfUnderwriter_PC',
                required: true,
                validator: ['required', 'positive', 'numeric'],
              },
            ]
          },
          {
            styleClasses: 'w-full',
            fields: [
              {
                disabled: () => this.readonly,
                type: 'vsCheckbox',
                styleClasses: 'vx-col w-1/3',
                label: 'Tax Due to Coverholder',
                model: 'TaxDuetoCoverholder',
                required: true,
              },
            ]
          }
        ],
      },
      formOptions: {
        validateAfterLoad: false,
        validateAfterChanged: true,
      },
    };
  },
};
</script>
